import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import {addGrid, getNoOfGridItems} from '@Components/base-grid';
import type {TextToImageGridProps} from '@Components/text-to-image-grid/text-to-image-grid.types';
import {Text, TextSize, TextType} from '@Components/text';
import {SkeletonBrick} from '@Components/skeleton-brick';
import {getAssetUrl} from '@Utils/s3.util';
import {LazyMasonryGrid} from '@Components/lazy-masonry-grid';
import type {LazyLoadingGetterParams, LazyLoadingResponse} from '@Libraries/pmw-stock-media-library';
import {getUserGeneratedImages} from '@Libraries/pmw-stock-media-library';
import {getGridItemForAIGeneratedImageV2} from '@Panels/stock-panel/stock-panel.library';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './text-to-image-grid.module.scss';

const INFOGRAPHIC_IMAGE_URL = getAssetUrl(`poster-editor-thumbnails/ai-text-to-image-empty-visual.png`);

const GRID_ITEMS_DEFAULT_HEIGHT = 160;

export function TextToImageGrid({isLoading = false, ...props}: TextToImageGridProps): ReactElement<any> {
  const [infographicLoaded, setInfographicLoaded] = useState(false);
  const dispatch = useAppDispatch();
  const numberOfGridItems = useAppSelector((state) => {
    return getNoOfGridItems(state, props.gridId);
  });
  const isInitialState = numberOfGridItems <= 0 && !isLoading;

  const getPreSearchInfographic = (): ReactElement<any> => {
    return (
      <div className={'flex-v-row flex-center flex-1'}>
        <img
          className={!infographicLoaded ? styles.infographicImageNotVisible : `spacing-m-t-5 ${styles.infographicImage}`}
          onLoad={() => {
            setInfographicLoaded(true);
          }}
          src={INFOGRAPHIC_IMAGE_URL}
          loading="lazy"
          alt={window.i18next.t('pmwjs_text_to_image_infographic_desc')}
        />
        {!infographicLoaded ? <SkeletonBrick className={`spacing-m-t-6 spacing-m-b-2 ${styles.infographicImage} radius-12`} /> : null}
        <Text type={TextType.BODY} className={`content-body text-center spacing-p-t-2`} size={TextSize.XSMALL} val={window.i18next.t('pmwjs_text_to_image_infographic_desc')} />
      </div>
    );
  };

  const getItems = async (params: LazyLoadingGetterParams): Promise<LazyLoadingResponse> => {
    const offset = Object.keys(params.offsets).reduce((agg, item) => {
      return agg + params.offsets[item];
    }, 0);

    const items = await getUserGeneratedImages(offset, params.limit);

    const gridItems = items.map(getGridItemForAIGeneratedImageV2);

    return {response: gridItems, offsetsData: {DEFAULT_SOURCE: gridItems.length}};
  };

  const getGrid = (): ReactElement<any> => {
    return (
      <>
        <LazyMasonryGrid
          {...props}
          getItems={getItems}
          key={props.gridId}
          gridId={props.gridId}
          defaultHeight={GRID_ITEMS_DEFAULT_HEIGHT}
          className={props.gridClasses}
          clearGridOnUnmount
        />
        {isInitialState ? getPreSearchInfographic() : null}
      </>
    );
  };

  useEffect(() => {
    dispatch(
      addGrid({
        id: props.gridId,
        gridSelectionGroupId: props.gridSelectionGroupId,
        items: [],
      })
    );
  }, []);

  return getGrid();
}
